import { useCallback, useMemo, type KeyboardEvent } from 'react';

import type { ListboxItem } from './Listbox';

/**
 * Shared ArrowUp/Down/Home/End/Enter stepping over `items`, skipping disabled
 * rows. Used internally by `Listbox`'s own roving mode, and exported so a
 * combobox-style caller (`AddRoleCombobox`) can wire the SAME stepping logic
 * to its external search input's `onKeyDown` while rendering `Listbox` itself
 * in controlled `activeId` mode.
 */
export function useListboxKeyboardNav<T>({
    items,
    activeId,
    onActiveIdChange,
    onSelect,
}: {
    items: ListboxItem<T>[];
    activeId: string | null;
    onActiveIdChange: (id: string | null) => void;
    onSelect: (item: ListboxItem<T>) => void;
}) {
    const enabled = useMemo(() => items.filter((item) => !item.disabled), [items]);

    return useCallback(
        (event: KeyboardEvent) => {
            if (enabled.length === 0) return;
            const currentIndex = enabled.findIndex((item) => item.id === activeId);

            const step = (nextIndex: number) => {
                event.preventDefault();
                onActiveIdChange(enabled[nextIndex].id);
            };

            switch (event.key) {
                case 'ArrowDown':
                    step(currentIndex < 0 ? 0 : Math.min(currentIndex + 1, enabled.length - 1));
                    return;
                case 'ArrowUp':
                    step(currentIndex < 0 ? enabled.length - 1 : Math.max(currentIndex - 1, 0));
                    return;
                case 'Home':
                    step(0);
                    return;
                case 'End':
                    step(enabled.length - 1);
                    return;
                case 'Enter': {
                    const active = enabled.find((item) => item.id === activeId);
                    if (active) {
                        event.preventDefault();
                        onSelect(active);
                    }
                    return;
                }
                default:
                    return;
            }
        },
        [enabled, activeId, onActiveIdChange, onSelect]
    );
}
